import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Paper, Container, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Collapse, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon, } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import datadrillhover from 'assets/images/selectedicons/Data Drill Down1.svg'
import FilterPanelContainer from "components/Filter/Filterpopup"
import SouthIcon from '@mui/icons-material/South';
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations";
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import LineChartsale from "components/Visualizations/LineChartsale"
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import AppHeader from "components/Header/AppHeader";
import FilterChip from "components/FilterChip/FilterChip";



const Dashboard = ({ subcategoryToken: propSubcategoryToken }) => {
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

    // State for API data

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);

    // Toggle sidenav from the page menu icon
    const isTogglingSidenavRef = useRef(false);
    const handleToggleSidenav = useCallback(() => {
        if (isTogglingSidenavRef.current) return;
        isTogglingSidenavRef.current = true;
        // Defer to next frame to avoid blocking heavy charts paint
        requestAnimationFrame(() => {
            setMiniSidenav(dispatch, !miniSidenav);
            setTimeout(() => {
                isTogglingSidenavRef.current = false;
            }, 150);
        });
    }, [dispatch, miniSidenav]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
            } catch (error) {
                console.error('Failed loading static mocks or transforming data:', error);
                setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [location.search, propSubcategoryToken]);


    //First section KPIscards data
    const Kpiscardsdata = [
        {
            "title": "Net Sales",
            "showIndicator": true,
            "values": [
                { "label": "MTD", "change": "2.1%", "value": "$65.2M", "status": "increase" },
                { "label": "QTD", "change": "3.8%", "value": "$180.6M", "status": "nochange" },
                { "label": "YTD", "change": "4.2%", "value": "$859.1M", "status": "increase" }
            ]
        },
        {
            "title": "Cost of Goods Sold",
            "showIndicator": true,
            "values": [
                { "label": "MTD", "change": "2.3%", "value": "$42.6M", "status": "increase" },
                { "label": "QTD", "change": "3.1%", "value": "$112.6M", "status": "nochange" },
                { "label": "YTD", "change": "3.4%", "value": "$514.7M", "status": "increase" }
            ]
        },
        {
            "title": "Gross Profit",
            "showIndicator": true,
            "values": [
                { "label": "MTD", "change": "1.8%", "value": "$39.8M", "status": "increase" },
                { "label": "QTD", "change": "3.9%", "value": "$118.5M", "status": "nochange" },
                { "label": "YTD", "change": "4.5%", "value": "$398.2M", "status": "increase" }
            ]
        },

        {
            "title": "EBIT",
            "showIndicator": true,
            "values": [
                { "label": "MTD", "change": "1.6%", "value": "$14.1M", "status": "increase" },
                { "label": "QTD", "change": "3.2%", "value": "$42.3M", "status": "nochange" },
                { "label": "YTD", "change": "4.1%", "value": "$158.0M", "status": "increase" }
            ]
        },
        {
            "title": "Closing Balance (Cash Position)",
            "showIndicator": true,
            "values": [
                { "label": "MTD", "change": "1.0%", "value": "$89.2M", "status": "increase" },
                { "label": "QTD", "change": "2.3%", "value": "$92.6M", "status": "nochange" },
                { "label": "YTD", "change": "3.0%", "value": "$100.2M", "status": "increase" }
            ]
        },
        {
            "title": "Stock Amount (Dollars)",
            "showIndicator": true,
            "values": [
                { "label": "MTD", "change": "1.2%", "value": "$95.6M", "status": "increase" },
                { "label": "QTD", "change": "2.4%", "value": "$112.5M", "status": "nochange" },
                { "label": "YTD", "change": "3.1%", "value": "$116.2M", "status": "increase" }
            ]
        },

        {
            "title": "Inventory Turnover (Units)",
            "showIndicator": false,
            "values": [
                { "label": "MTD", "change": "1.0%", "value": "7.7", "status": "increase" },
                { "label": "QTD", "change": "2.2%", "value": "7.8", "status": "nochange" },
                { "label": "YTD", "change": "2.9%", "value": "7.9", "status": "increase" }
            ]
        },
        {
            "title": "Forecast Accuracy (MAPE)",
            "showIndicator": false,
            "values": [
                { "label": "MTD", "change": "0.3%", "value": "14.8%", "status": "increase" },
                { "label": "QTD", "change": "0.7%", "value": "15.2%", "status": "nochange" },
                { "label": "YTD", "change": "1.4%", "value": "16.1%", "status": "increase" }
            ]
        },
        {
            "title": "Freight as a Percentage of Revenue",
            "showIndicator": false,
            "values": [
                { "label": "MTD", "change": "0.8%", "value": "7.4%", "status": "increase" },
                { "label": "QTD", "change": "1.3%", "value": "7.8%", "status": "nochange" },
                { "label": "YTD", "change": "-2.4%", "value": "7.1%", "status": "decrease" }
            ]
        }
    ]



    //Second section TimeSeriescards data
    const timeSeriesCardsdata = [
        {
            title: "Net Sales",
            values: [
                { label: "Nov 2024", change: "+4.2%", value: "$62.5M", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "", value: "$65.2M", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+3.3%", value: "$63.0M", status: "increase", arrow: "left" }
            ],
            series: [55, 58, 60, 62, 63, 65],
            "showIndicator": true,
        },

        {
            title: "Cost of Goods Sold",
            values: [
                { label: "Nov 2024", change: "+3.4%", value: "$41.2M", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+1.9%", value: "$42.6M", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-1.9%", value: "$43.4M", status: "decrease", arrow: "left" }
            ],
            series: [35, 38, 39, 40, 42, 43],
            "showIndicator": true,
        },

        {
            title: "Gross Profit",
            values: [
                { label: "Nov 2024", change: "+5.2%", value: "$37.8M", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+3.9%", value: "$39.8M", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-3.7%", value: "$41.3M", status: "decrease", arrow: "left" }
            ],
            series: [30, 32, 34, 36, 38, 40],
            "showIndicator": true,
        },

        {
            title: "EBIT",
            values: [
                { label: "Nov 2024", change: "+4.2%", value: "$13.5M", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+1.6%", value: "$14.1M", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-3.4%", value: "$14.6M", status: "decrease", arrow: "left" }
            ],
            series: [11, 12, 13, 13.5, 14, 14.6],
            "showIndicator": true,
        },

        {
            title: "Closing Balance (Cash Position)",
            values: [
                { label: "Nov 2024", change: "+2.4%", value: "$87.1M", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+2.4%", value: "$89.2M", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-0.9%", value: "$90.0M", status: "decrease", arrow: "left" }
            ],
            series: [82, 84, 86, 87, 89, 90],
            "showIndicator": true,
        },

        {
            title: "Stock Amount (Dollars)",
            values: [
                { label: "Nov 2024", change: "+10.5%", value: "$86.5M", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+2.4%", value: "$95.6M", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-2.6%", value: "$98.1M", status: "decrease", arrow: "left" }
            ],
            series: [75, 80, 82, 86, 95, 98],
            "showIndicator": true,
        },

        {
            title: "Inventory Turnover (Units)",
            values: [
                { label: "Nov 2024", change: "+2.2%", value: "7.53", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+2.9%", value: "7.70", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.6%", value: "7.65", status: "increase", arrow: "left" }
            ],
            series: [7.2, 7.3, 7.4, 7.5, 7.6, 7.7],
            "showIndicator": false,
        },

        {
            title: "Forecast Accuracy (MAPE)",
            values: [
                { label: "Nov 2024", change: "+1.1%", value: "13.7%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+0.5%", value: "14.8%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.5%", value: "14.3%", status: "increase", arrow: "left" }
            ],
            series: [12.5, 13, 13.5, 14, 14.3, 14.8],
            "showIndicator": false,
        },

        {
            title: "Freight as a Percentage of Revenue",
            values: [
                { label: "Nov 2024", change: "+0.5%", value: "6.9%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: "+0.5%", value: "7.4%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-0.2%", value: "7.6%", status: "decrease", arrow: "left" }
            ],
            series: [6.5, 6.7, 6.8, 6.9, 7.4, 7.6],
            "showIndicator": false,
        }
    ];

    //Third section highlightCards 
    const highlightCards = [
        {
            title: "#1 Sub Brand by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "105.7M",
            topRightLabel: "NET SALES",
            topRightValue: "$485.3M",
            bigTitle: "C4 Performance",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "97.3M"
        },
        {
            title: "#2 Sub Brand by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "42.0M",
            topRightLabel: "NET SALES",
            topRightValue: "$105.0M",
            bigTitle: "Bloom Sparkling Energy",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "36.5M"
        },
        {
            title: "#3 Sub Brand by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "6.8M",
            topRightLabel: "NET SALES",
            topRightValue: "$178.5M",
            bigTitle: "XTEND",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "6.1M"
        },

        {
            title: "#1 Distributor by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "245.0M",
            topRightLabel: "NET SALES",
            topRightValue: "$510.3M",
            bigTitle: "American Bottling Company",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "228.4M"
        },
        {
            title: "#2 Distributor by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "72.5M",
            topRightLabel: "NET SALES",
            topRightValue: "$148.4M",
            bigTitle: "Great Basin Beverage",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "68.1M"
        },
        {
            title: "#3 Distributor by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "86.2M",
            topRightLabel: "NET SALES",
            topRightValue: "$172.3M",
            bigTitle: "Watkins Distributing",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "79.4M"
        },

        {
            title: "#1 Retailer by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "190.4M",
            topRightLabel: "NET SALES",
            topRightValue: "$392.7M",
            bigTitle: "7-Eleven",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "174.3M"
        },
        {
            title: "#2 Retailer by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "128.0M",
            topRightLabel: "NET SALES",
            topRightValue: "$262.5M",
            bigTitle: "Walmart",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "121.4M"
        },
        {
            title: "#3 Retailer by Profitability",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "22.9M",
            topRightLabel: "NET SALES",
            topRightValue: "$91.4M",
            bigTitle: "GNC",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "21.0M"
        },

        {
            title: "#1 Backordered SKU",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "4.2M",
            topRightLabel: "NET SALES",
            topRightValue: "$9.8M",
            bigTitle: "C4 Frozen Bombsicle 16oz",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "3.9M"
        },
        {
            title: "#2 Backordered SKU",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "3.6M",
            topRightLabel: "NET SALES",
            topRightValue: "$8.4M",
            bigTitle: "C4 Starburst Strawberry 16oz",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "3.3M"
        },
        {
            title: "#3 Backordered SKU",
            topLeftLabel: "UNITS SOLD",
            topLeftValue: "2.9M",
            topRightLabel: "NET SALES",
            topRightValue: "$6.7M",
            bigTitle: "C4 Orange Slice 16oz",
            bottomLabel: "UNITS SOLD LY",
            bottomValue: "2.6M"
        }
    ];



    const [openFilters, setOpenFilters] = useState(false);
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Filter state
    const [activeFilter, setActiveFilter] = useState('All');
    const [activeIdx, setActiveIdx] = React.useState(0);

    const [filterLoading, setFilterLoading] = useState(false);

    // Define filter presets for each quick filter
    const getFilterPreset = useCallback((filterName) => {
        const allCompanies = ["Nutrabolt", "Bloom"];
        const allCategories = ["Functional Beverage", "Active Nutrition"];
        const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
        const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
        const allRoutes = ["Ecommerce", "Direct", "DSD"];
        const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

        switch (filterName) {
            case 'All':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Nutrabolt':
                return {
                    Company: ["Nutrabolt"],
                    Category: allCategories,
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Bloom':
                return {
                    Company: ["Bloom"],
                    Category: ["Functional Beverage"],
                    Brand: ["Bloom"],
                    "Sub-Brand": ["Bloom Sparkling Energy", "Bloom Pop"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Functional Beverage':
                return {
                    Company: allCompanies,
                    Category: ["Functional Beverage"],
                    Brand: ["C4", "Bloom"],
                    "Sub-Brand": ["C4 Performance", "C4 Smart Energy", "C4 Ultimate", "Bloom Sparkling Energy", "Bloom Pop"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Active Nutrition':
                return {
                    Company: ["Nutrabolt"],
                    Category: ["Active Nutrition"],
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": ["C4", "XTEND"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            default:
                return null;
        }
    }, []);

    const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));

    // Handle filter click with full-page loader
    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);
        setLoading(true); // Show full-page loader

        // Simulate loading delay
        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            // Set the preset - this will trigger FilterPanelContainer to apply it
            if (preset) {
                setFilterPresets(preset);
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);
            setLoading(false); // Hide full-page loader

            // Open filter panel for "Custom" filter
            if (filter === 'Custom') {
                setOpenFilters(true);
            }
        }, 800);
    };

    // Handle filter changes from FilterPanelContainer
    const handleFiltersChange = useCallback((newFilters) => {
        // Don't check for preset match if we're already in Custom mode
        // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
        if (activeFilter === 'Custom') return;

        // Check if the current filters match any preset
        const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
        let matchedPreset = null;

        for (const presetName of presetNames) {
            const preset = getFilterPreset(presetName);
            if (preset) {
                const isMatching = Object.keys(preset).every(key => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();
                    return presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx]);
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }
        }

        // If filters don't match any preset, switch to Custom
        if (!matchedPreset && activeFilter !== 'Custom') {
            setActiveFilter('Custom');
            setFilterPresets(null);
        }
    }, [activeFilter, getFilterPreset]);



    const kpiBriefData = [
        {

            title: "Month",
            value: "$ 65.2 M",
            lyValue: "59.12 M",
            pct: "5.8%",
            positive: false,
            status: "decrease",
            glvalue: "$ 85.5 M"
        },
        {
            title: "Quarter",
            value: "$ 180.6 M",
            lyValue: "160.29 M",
            pct: "11.9%",
            positive: true,
            status: "increase",
            glvalue: "$ 207.3 M"
        },
        {
            title: "Year",
            value: "$ 859.1 M",
            lyValue: "823.03 M",
            pct: "5.6%",
            positive: true,
            status: "increase",
            glvalue: "$ 882.8 M"
        }
    ];


    // -------------------------------------------------------------
    // PIE CHART DATA (LEGEND USES SAME DATA)
    // -------------------------------------------------------------

    const pieCategory = [
        { label: "Active Nutrition", value: 42, color: "#0255D6" },
        { label: "Functional Beverage", value: 58, color: "#8CC63F" },

    ];

    const pieSubBrands = [
        { label: "C4 Powder", value: 8, color: "#C2CF2D" },
        { label: "Bloom Pop", value: 6, color: "#59A671" },
        { label: "Bloom Sparkling Energy", value: 12, color: "#C393E4" },
        { label: "XTEND", value: 8, color: "#C5C5C5" },
        { label: "Cellucor", value: 10, color: "#747474" },
        { label: "C4 Ultimate", value: 6, color: "#1A1A1A" },
        { label: "C4 Smart Energy", value: 5, color: "#1BA4E6" },
        { label: "C4 Performance", value: 45, color: "#ECD01C" },
    ];
    const pieDistributors = [
        { label: "Other", value: 8, color: "#000000" },
        { label: "Brown Bottling Group", value: 8, color: "#3948AB" },
        { label: "The Lewis Bear Company", value: 7, color: "#F4511E" },
        { label: "Great Basin Beverage", value: 10, color: "#C52161" },
        { label: "Watkins Distributing", value: 12, color: "#00897B" },
        { label: "The American Bottling Company (KDP)", value: 55, color: "#7B21A3" },
    ];

    const pieRetailers = [
        { label: "Other", value: 12, color: "#000000" },
        { label: "HEB", value: 9, color: "#7B21A3" },
        { label: "Circle K", value: 13, color: "#C52161" },
        { label: "Kroger", value: 16, color: "#8CC63F" },
        { label: "Walmart", value: 23, color: "#1E63DA" },
        { label: "7-Eleven", value: 27, color: "#E3A403" },



    ];



    const subBrandChartData = {
        title: "Net Sales by Sub Brand",

        xLabels: [
            "Dec. 2025",
            "Jan. 2025",
            "Feb. 2025",
            "Mar. 2025",
            "Apr. 2025",
            "May. 2025",
            "Jun. 2025",
            "Jul. 2025",
            "Aug. 2025",
            "Sep. 2025",
            "Oct. 2025",
            "Nov. 2025"
        ],

        series: [
            { label: "C4 Powder", color: "#C2CF2D" },
            { label: "C4 Performance", color: "#ECD01C" },
            { label: "C4 Smart Energy", color: "#1BA4E6" },
            { label: "C4 Ultimate", color: "#1A1A1A" },
            { label: "Cellucor", color: "#747474" },
            { label: "XTEND", color: "#C5C5C5" },
            { label: "Bloom Sparkling Energy", color: "#C393E4" },
            { label: "Bloom Pop", color: "#59A671" }
        ],

        dataMatrix: [
            [4.97, 33.20, 1.69, 5.82, 8.25, 6.66, 6.66, 3.35],
            [5.30, 35.40, 1.80, 6.20, 8.80, 7.10, 7.10, 3.50],
            [5.55, 37.05, 1.88, 6.49, 9.21, 7.43, 7.43, 3.66],
            [5.25, 35.07, 1.78, 6.14, 8.72, 7.04, 7.04, 3.47],
            [5.72, 38.23, 1.94, 6.69, 9.50, 7.67, 7.67, 3.78],
            [5.89, 39.38, 2.00, 6.89, 9.78, 7.90, 7.90, 3.89],
            [6.37, 42.56, 2.16, 7.46, 10.58, 8.54, 8.54, 4.21],
            [5.64, 37.70, 1.92, 6.60, 9.38, 7.56, 7.56, 3.73],
            [6.50, 43.45, 2.21, 7.61, 10.80, 8.71, 8.71, 4.29],
            [5.71, 38.12, 1.94, 6.68, 9.47, 7.64, 7.64, 3.77],
            [5.63, 37.62, 1.91, 6.59, 9.35, 7.54, 7.54, 3.72],
            [6.32, 42.23, 2.15, 7.39, 10.49, 8.47, 8.47, 4.17]
        ]

    };

    // YOUR SAME SUB-BRAND DATA (converted to line-scale values)
    const lineChartSubBrandData = {
        title: "Cases Ordered vs Forecasted by Sub-Brand",

        xLabels: [
            "Dec. 2025", "Jan. 2025", "Feb. 2025", "Mar. 2025",
            "Apr. 2025", "May. 2025", "Jun. 2025", "Jul. 2025",
            "Aug. 2025", "Sep. 2025", "Oct. 2025", "Nov. 2025"
        ],

        series: [
            { label: "C4 Powder", color: "#C2CF2D" },
            { label: "C4 Performance", color: "#ECD01C" },
            { label: "C4 Smart Energy", color: "#1BA4E6" },
            { label: "C4 Ultimate", color: "#1A1A1A" },
            { label: "Cellucor", color: "#747474" },
            { label: "XTEND", color: "#C5C5C5" },
            { label: "Bloom Sparkling Energy", color: "#C393E4" },
            { label: "Bloom Pop", color: "#59A671" }
        ],

        //  NOW supports ACTUAL + FORECAST
        dataMatrix: {
            actual: [
                [2.8, 4.0, 1.6, 2.2, 1.7, 1.3, 1.0, 0.5], // Dec
                [3.0, 4.2, 1.7, 2.5, 1.8, 1.4, 1.1, 0.6], // Jan
                [2.9, 4.5, 1.7, 2.3, 1.6, 1.3, 1.1, 0.8], // Feb
                [3.1, 4.3, 1.6, 2.6, 1.7, 1.2, 1.0, 0.6], // Mar
                [2.7, 4.0, 1.8, 2.4, 1.6, 1.3, 1.2, 0.9], // Apr
                [3.2, 4.3, 2.0, 2.9, 1.9, 1.5, 1.3, 1.1], // May
                [3.0, 4.4, 1.9, 2.6, 1.8, 1.4, 1.1, 0.9], // Jun
                [2.9, 4.1, 1.7, 2.7, 1.7, 1.2, 1.3, 1.0], // Jul
                [3.1, 4.4, 1.9, 2.8, 1.8, 1.5, 1.2, 1.2], // Aug
                [3.0, 4.3, 2.1, 2.6, 1.9, 1.5, 1.3, 1.3], // Sep
                [3.2, 4.2, 2.0, 2.9, 1.9, 1.6, 1.4, 1.4], // Oct
                [3.4, 4.6, 2.1, 2.8, 2.0, 1.7, 1.5, 1.5]  // Nov
            ],

            forecast: [
                [2.6, 3.8, 1.5, 2.0, 1.5, 1.2, 0.9, 0.4], // Dec (sample)
                [2.8, 4.0, 1.6, 2.3, 1.7, 1.3, 1.0, 0.5], // Jan
                [2.7, 4.2, 1.6, 2.1, 1.5, 1.2, 1.0, 0.7], // ...
                [3.0, 4.1, 1.5, 2.4, 1.6, 1.1, 0.9, 0.5],
                [2.5, 3.8, 1.7, 2.2, 1.5, 1.2, 1.1, 0.8],
                [3.0, 4.1, 1.8, 2.7, 1.8, 1.4, 1.2, 1.0],
                [2.8, 4.2, 1.8, 2.4, 1.7, 1.3, 1.0, 0.8],
                [2.7, 4.0, 1.6, 2.5, 1.6, 1.1, 1.2, 0.9],
                [2.9, 4.1, 1.7, 2.6, 1.7, 1.4, 1.1, 1.1],
                [2.8, 4.0, 1.9, 2.5, 1.8, 1.4, 1.2, 1.2],
                [3.0, 4.1, 1.9, 2.7, 1.8, 1.5, 1.3, 1.3],
                [3.2, 4.4, 2.0, 2.7, 1.9, 1.6, 1.4, 1.4]
            ]
        }
    };

    const netGrossCashChartData = {
        title: "Net Sales, Gross Profit, and Cash Balance",

        xLabels: [
            "Dec. 2025", "Jan. 2025", "Feb. 2025", "Mar. 2025",
            "Apr. 2025", "May. 2025", "Jun. 2025", "Jul. 2025",
            "Aug. 2025", "Sep. 2025", "Oct. 2025", "Nov. 2025"
        ],

        series: [
            { key: "net", label: "Net Sales", color: "#62BDFF" },
            { key: "gross", label: "Gross Profit", color: "#317FBB" },
            { key: "cash", label: "Cash Balance", color: "#449C7D", dashed: true }
        ],

        // FOLLOWS ORDER OF xLabels, AND ORDER OF series KEYS
        dataMatrix: [
            [68.3, 18.4, 56.4], // Dec
            [74.5, 22.1, 50.2], // Jan
            [76.2, 22.8, 45.6], // Feb
            [69.1, 18.9, 53.4], // Mar
            [81.3, 22.9, 50.3], // Apr
            [85.1, 24.1, 62.3], // May
            [89.2, 25.0, 60.1], // Jun
            [88.5, 24.8, 77.2], // Jul
            [92.4, 26.2, 79.1], // Aug
            [79.6, 21.2, 63.4], // Sep
            [81.2, 22.3, 60.5], // Oct
            [88.7, 23.8, 72.4]  // Nov
        ],

        yTicks: [0, 20, 40, 60, 80, 100],  // CUSTOM SCALE
        fillColor: "#62BBFF"  // Background fill color for the chart
    };

    const inventoryChartData = {
        title: "Cash Balance and Inventory Value",

        xLabels: [
            "Dec. 2025", "Jan. 2025", "Feb. 2025", "Mar. 2025",
            "Apr. 2025", "May. 2025", "Jun. 2025", "Jul. 2025",
            "Aug. 2025", "Sep. 2025", "Oct. 2025", "Nov. 2025"
        ],

        series: [
            { key: "cashBalance", label: "Cash Balance", type: "line", color: "#56BC4C" },
            { key: "stockAmount", label: "Stock Amount (Dollars)", type: "line", color: "#8A2BE2" },
            { key: "cashIn", label: "Cash In", type: "bar", color: "#56BC4C" },
            { key: "cashOut", label: "Cash Out", type: "bar", color: "#E74C3C" }
        ],

        // DataMatrix follows SAME order as xLabels and series
        dataMatrix: [
            [56.4, 62.6, 30, 44], // Dec
            [50.2, 65.6, 25, 37], // Jan
            [45.4, 67.2, 35, 31], // Feb
            [53.4, 68.0, 40, 29], // Mar
            [50.3, 78.1, 23, 30], // Apr
            [62.3, 79.4, 47, 34], // May
            [60.1, 98.1, 36, 41], // Jun
            [72.2, 90.3, 43, 29], // Jul
            [79.1, 95.1, 38, 44], // Aug
            [63.4, 87.2, 36, 43], // Sep
            [60.5, 81.5, 42, 25], // Oct
            [70.4, 80.1, 37, 26]  // Nov
        ],

        // You can choose your own scale
        yTicks: [0, 20, 40, 60, 80, 100]
    };

    const raw = [
        { label: "Gross Sales", value: 900 },
        { label: "Trade Spend", value: -100 },
        { label: "Net Sales", value: 800 },
        { label: "COGS", value: -450 },
        { label: "Gross Profit", value: 350 },
        { label: "Operating Exp.", value: -200 },
        { label: "EBIT", value: 150 }
    ];
    const [hover, setHover] = React.useState(false);

    return (


        <Box sx={{
            ml: 0,
            transition: "margin-left 0.25s ease",
            "& .MuiBox-root": { backgroundImage: "none" }
        }} style={{
            // backgroundColor: '#f8f9fa',

            padding: isMobile ? '8px' : '0px 16px 0px 16px',
            // paddingBottom: '100px',
            fontFamily: 'inter !important'
        }} >
            {/* Loading Backdrop */}
            <Backdrop
                open={loading}
                sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>

            <Box
                sx={{
                    height: {
                        xs: "calc(100dvh - 140px)", // mobile header
                        md: "calc(100vh - 64px)",  // desktop header
                    },

                    overflowY: "auto",
                    // px: isMobile ? 1 : 2,
                    pb: "100px",
                    /* smooth scrolling */
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch", // iOS momentum
                    // backgroundColor: "green",
                    /* hide scrollbar */
                     scrollbarWidth: "none",          // Firefox
                     msOverflowStyle: "none",         // IE / Edge
                     "&::-webkit-scrollbar": {
                        display: "none",               // Chrome / Safari
                    },
                }}
            >




                {/* Section Title: KPI's */}
                <Typography sx={{ mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px', }}>My Dashboard / </span><span style={{ fontWeight: 200, fontSize: '16px', }}>KPI's</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <KpiMetricscards cardsData={Kpiscardsdata} kpiBriefData={kpiBriefData} />

                {/* Section Title: Time Series */}
                <Typography sx={{ mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px' }}>My Dashboard / </span><span style={{ fontWeight: 200, fontSize: '16px' }}>Time Series</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

                {/* Section Title: Highlights with YTD/QTD/MTD buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 3 }}>
                    <Typography sx={{ fontSize: '16px' }}>
                        <span style={{ color: '#1672B980', fontWeight: 200, fontSize: '16px', }}>My Dashboard / </span> <span style={{ fontWeight: 200, fontSize: '16px', }}>Highlights</span>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {['YTD', 'QTD', 'MTD'].map((label, idx) => (
                            <FilterChip
                                key={label}
                                label={label}
                                idx={idx}
                                activeIdx={activeIdx}
                                onClick={setActiveIdx}
                            />


                        ))}
                    </Box>

                </Box>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <HighlightsCards data={highlightCards} />
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '16px' }}>
                            <span style={{ color: "#1672B980", fontWeight: 200, fontSize: '16px' }}>My Dashboard / </span> <span style={{ fontWeight: 200, fontSize: '16px' }}>Visualizations</span>
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }} className='visualization-filter-chips'>
                            {["YTD", "QTD", "MTD"].map((label, idx) => (
                                <FilterChip
                                    key={label}
                                    label={label}
                                    idx={idx}
                                    activeIdx={activeIdx}
                                    onClick={setActiveIdx}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                </Box>
                {/* Section Title: Visualizations */}

                <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                        mt: "26px !important",
                        alignItems: "stretch"   // keeps all cards same height
                    }}
                >
                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Gross Profit by Category" data={pieCategory} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Gross Profit by Sub Brand" data={pieSubBrands} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Top 5 Distributors by Profitability" data={pieDistributors} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={3} xl={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Top 5 Retailers by Profitability" data={pieRetailers} />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        <Barcharts
                            title={subBrandChartData.title}
                            xLabels={subBrandChartData.xLabels}
                            series={subBrandChartData.series}
                            dataMatrix={subBrandChartData.dataMatrix}
                            yTicks={[0, 20, 40, 60, 80, 100]}
                            yAxisLabel="Net Sales ($M)"
                            rounded={true}
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        <LineChartSubBrand
                            title={lineChartSubBrandData.title}
                            xLabels={lineChartSubBrandData.xLabels}
                            series={lineChartSubBrandData.series}
                            dataMatrix={lineChartSubBrandData.dataMatrix}

                            yTicks={[0, 1, 2, 3, 4, 5]}
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        <LineChartsale
                            title={netGrossCashChartData.title}
                            xLabels={netGrossCashChartData.xLabels}
                            series={netGrossCashChartData.series}
                            dataMatrix={netGrossCashChartData.dataMatrix}
                            yTicks={netGrossCashChartData.yTicks}
                            fillColor={netGrossCashChartData.fillColor}
                            yAxisLabel=" Dollars ($M)"
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        <InventoryCharts
                            title={inventoryChartData.title}
                            xLabels={inventoryChartData.xLabels}
                            series={inventoryChartData.series}
                            dataMatrix={inventoryChartData.dataMatrix}
                            yTicks={inventoryChartData.yTicks}
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "30px !important" }}>
                    <Grid item xs={12}>
                        <WaterfallChart
                            title="Revenue to EBIT "
                            rawData={raw}

                        />
                    </Grid>
                </Grid>

            </Box>




        </Box>




    );
};

export default Dashboard;